import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import heroImage from '../../../assets/christmas25/Wreath.jpg';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 80px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  p { margin: 0 0 1rem; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;
  &:hover { opacity: 0.95; transform: translateY(-2px); }
`;

const WreathMaking = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: 'Christmas Wreath Making at Belvoir Castle 2025',
    description: 'Join Christmas wreath making workshop at Belvoir Castle 2025 with Sarah Youngman. £60pp includes materials, bubbles & mince pie. Christmas crafts in Leicestershire. Book now!',
    canonical: 'https://www.belvoircastle.com/christmas/wreath-making'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Christmas Wreath Making Workshop at Belvoir Castle 2025',
    description: 'Join us for a magical evening of creativity and festive cheer at Belvoir Bistro as we welcome the incredibly talented Sarah Youngman from Sage and Satin Florals. Learn how to craft your very own beautiful Christmas wreath using seasonal foliage, natural decorations, and expert floristry tips.',
    image: [
      "https://www.belvoircastle.com/christmas25/Wreath.jpg"
    ],
    startDate: '2025-11-27T18:00:00',
    endDate: '2025-12-05T21:00:00',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: 'Belvoir Bistro, Belvoir Castle',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Castle',
        addressLocality: 'Grantham',
        addressRegion: 'Leicestershire',
        postalCode: 'NG32 1PE',
        addressCountry: 'GB'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 52.8931,
        longitude: -0.7792
      }
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
      telephone: '+441476871001',
      email: 'info@belvoircastle.com'
    },
    performer: {
      '@type': 'Person',
      name: 'Sarah Youngman',
      jobTitle: 'Florist',
      worksFor: {
        '@type': 'Organization',
        name: 'Sage and Satin Florals'
      }
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'Wreath Making Workshop',
        price: '60',
        priceCurrency: 'GBP',
        availability: 'https://schema.org/LimitedAvailability',
        validFrom: '2025-11-27',
        validThrough: '2025-12-05',
        url: 'https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=7ad3bf3e-b399-4f52-a8f9-7a9064fdd69f',
        description: 'Includes all materials, a glass of bubbles and a festive mince pie'
      }
    ],
    audience: {
      '@type': 'Audience',
      audienceType: 'Adults and families'
    },
    keywords: 'Christmas wreath making, wreath workshop, Christmas crafts, floristry workshop, Christmas activities Leicestershire, festive crafts, Christmas workshop, Belvoir Castle events',
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Christmas wreath making, wreath workshop Leicestershire, Christmas crafts, floristry workshop, Christmas activities, festive crafts, Christmas workshop, Belvoir Castle events, wreath making class, Christmas DIY" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href={meta.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/Wreath.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Christmas Wreath Making at Belvoir Castle" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/Wreath.jpg" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Christmas Wreath Making at Belvoir Castle" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Christmas Wreath Making at Belvoir Castle" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Wreath Making Workshop",
                "item": meta.canonical
              }
            ]
          })}
        </script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>Limited Spaces</Badge>
          <Title>Wreath Making Workshop</Title>
          <Strapline>At Belvoir Bistro, Retail Village</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <Card>
          <SectionTitle>About the Christmas Wreath Making Workshop</SectionTitle>
          <Body>
            <p>Join us for a magical evening of Christmas creativity and festive cheer at Belvoir Bistro as we welcome the incredibly talented Sarah Youngman from Sage and Satin Florals. Learn how to craft your very own beautiful Christmas wreath using seasonal foliage, natural decorations, and expert floristry tips in this exclusive Christmas workshop.</p>
            <p>Whether you're coming with friends, family, or flying solo, it's the perfect way to kick off the Christmas season in style with this unique Christmas craft experience in Leicestershire.</p>
          </Body>
          <CTAButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=7ad3bf3e-b399-4f52-a8f9-7a9064fdd69f">Book Now</CTAButton>
        </Card>

        <Card>
          <SectionTitle>Key Information</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <div>Thursday 27 November & Friday 5 December 2025</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Time</InfoLabel>
              <div>6:00 PM</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Price</InfoLabel>
              <div>£60 per person</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Includes</InfoLabel>
              <div>All materials, a glass of bubbles and a festive mince pie</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <div>Belvoir Bistro, Retail Village</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Capacity</InfoLabel>
              <div>20 spaces per event</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <CTAButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=7ad3bf3e-b399-4f52-a8f9-7a9064fdd69f">Book Now</CTAButton>
            </InfoItem>
          </InfoGrid>
        </Card>
      </Content>

      <ImageSlant 
        image={heroImage}
        title="Complete Your Christmas Evening"
        description="Add Christmas Afternoon Tea to complete your wreath making workshop evening at Belvoir Castle."
        buttonText="Book Christmas Afternoon Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default WreathMaking;

